/* The consents an applicant ticks on the review step, per product/channel.
 *
 * ONE definition drives all three uses — the tick boxes, the "can I submit
 * yet?" test, and the message shown when they haven't ticked. They used to be
 * three hand-maintained copies of the same list inside steps.jsx; adding the
 * commercial set made that a drift risk worth removing.
 *
 * MIRROR: server/lib/profiles.js getServerProfile().validation.consents holds
 * the required set the server enforces. `canonical` lists the key(s) the
 * server validates for that tick (canonical.js maps form field -> canonical
 * consent) — a list because one tick can accept two documents. Concatenated in
 * order, the req:true entries must equal the server's list for that product.
 * scripts/api-test.mjs asserts the two sides agree.
 */
import React from "react";
import { legalUrl } from "../lib/legal.js";
import { BRAND, legalEntityName } from "../brand.js";

const Doc = ({ href, children }) => (
  <a href={legalUrl(href)} target="_blank" rel="noopener" className="pf-consent-link" onClick={(ev) => ev.stopPropagation()}>{children}</a>
);

/* Consumer (car / personal): the NCCP set — credit check, privacy + data
   exchange (one tick, both documents), credit guide, optional marketing. */
const CONSUMER = [
  {
    k: "consentCreditCheck", canonical: ["creditCheck"], req: true,
    err: "Please consent to the credit check (marked *) to submit.",
    label: <React.Fragment>I consent to a credit check being performed.</React.Fragment>,
  },
  {
    // One tick covers privacy + data exchange (both documents linked), so it
    // records BOTH acceptances — `also` mirrors the value across.
    k: "consentPrivacyPolicy", canonical: ["privacyPolicy", "dataExchange"], also: ["consentDataExchange"], req: true,
    err: "Please accept the Privacy Policy and Consent to Exchange Data (marked *) to submit.",
    label: <React.Fragment>I agree to {BRAND.name}'s <Doc href="/privacy">Privacy Policy</Doc> and <Doc href="/consent-to-exchange-data">Consent to the Exchange of Data</Doc>.</React.Fragment>,
  },
  {
    k: "consentCreditGuide", canonical: ["creditGuide"], req: true,
    err: "Please confirm you have read the Credit Guide (marked *) to submit.",
    label: <React.Fragment>I have read and understand {BRAND.name}'s <Doc href="/credit-guide">Credit Guide</Doc>.</React.Fragment>,
  },
  { k: "consentComms", canonical: ["marketing"], req: false, label: <React.Fragment>I'm happy to receive electronic communications.</React.Fragment> },
];

/* Commercial (asset finance): a DIFFERENT legal entity (brand.config.js
   legal.commercial) and a different document set. Business-purpose lending is outside
   the NCCP, so there is no Credit Guide; the single Privacy Consent covers
   what the consumer Privacy Policy and Consent to Exchange Data cover, and
   carries its own Electronic Communications Consent, which is a required
   acknowledgement (how we serve notices) — NOT the optional marketing opt-in
   below it. Never show a business applicant the consumer documents. */
const COMMERCIAL = [
  {
    k: "consentCreditCheck", canonical: ["creditCheck"], req: true,
    err: "Please consent to the credit checks (marked *) to submit.",
    label: <React.Fragment>I consent to credit checks being performed on the business and on each person named in this application.</React.Fragment>,
  },
  {
    k: "consentCommercialPrivacy", canonical: ["commercialPrivacy"], req: true,
    err: "Please accept the Privacy Consent (marked *) to submit.",
    label: <React.Fragment>I have read and agree to {legalEntityName("commercial")}'s <Doc href="/commercial-privacy-consent">Privacy Consent</Doc>, and where I have given information about another person I am authorised to do so.</React.Fragment>,
  },
  {
    k: "consentElectronic", canonical: ["electronic"], req: true,
    err: "Please consent to receiving documents electronically (marked *) to submit.",
    label: <React.Fragment>I consent to receiving notices and documents electronically, as set out in the <Doc href="/commercial-privacy-consent">Electronic Communications Consent</Doc>.</React.Fragment>,
  },
  { k: "consentComms", canonical: ["marketing"], req: false, label: <React.Fragment>I'm happy to receive news and offers from {BRAND.name}.</React.Fragment> },
];

/* PLACEHOLDER (broker channel): authority declaration — optional until the
   compliance wording is settled, then it moves into the required set (here and
   the server profile consent list together). Leads the list because it's the
   broker's own declaration, not their client's. */
const BROKER_DECLARATION = {
  k: "consentBrokerDeclaration", canonical: ["brokerDeclaration"], req: false,
  label: <React.Fragment>I confirm I hold my client's authority to submit this application on their behalf.</React.Fragment>,
};

export function consentsFor({ product = "car", channel = "direct" } = {}) {
  const base = product === "commercial" ? COMMERCIAL : CONSUMER;
  return channel === "broker" ? [BROKER_DECLARATION, ...base] : base;
}

/* Every field a consent writes — the tick plus anything it mirrors. A draft
   resumed with only half a mirrored pair set would pass a naive check here and
   then fail server-side, so `also` counts too. */
const fields = (c) => [c.k, ...(c.also || [])];

export const consentsSatisfied = (f, list) => list.every((c) => !c.req || fields(c).every((k) => f[k] === true));

export function consentsError(f, list) {
  const missing = list.filter((c) => c.req && !fields(c).every((k) => f[k] === true));
  if (!missing.length) return "";
  if (missing.length > 1) return "Please tick all required consents (marked *) to submit.";
  return missing[0].err || "Please tick all required consents (marked *) to submit.";
}
